import { ClipboardButton, FvDate, FvNum, Icon } from '@fv/client-components'
import {
  formatNumber,
  getDistanceDisplay,
  totalShipmentWeight,
} from '@fv/client-core'
import type { PropsWithLoad } from '@fv/client-types'
import { fvDate, fvDateUtc } from '@fv/dates'
import { displayPricingType } from '@fv/models'

import { HasPermission } from '@/features/auth'
import { HideForVendor } from '@/features/auth/HasRole'
import { ChargesSlider } from '@/features/charges/ChargesSlider'
import { ChargesToggle } from '@/features/charges/ChargesToggle'
import { useChargeList } from '@/features/charges/useChargeList'
import { GLCodeEditor } from '@/features/gl-codes/GLCodeEditor'
import { RefNumCarrierInfo } from '@/features/ref-nums/RefNumCarrierInfo'
import { RefNumsEditor } from '@/features/ref-nums/RefNumsEditor'
import { useAccountSettings } from '@/features/settings/account-settings/hooks'
import { LoadUserDisplay } from '@/features/shipment-list/LoadUserDisplay'
import { VendorMarkupDescription } from '@/features/shipment-list/VendorShipmentIndicator'
import { useAppContext, useLocale, useTypeNames } from '@/hooks/settings'
import { useRefNumsByType } from '@/hooks/shipments'
import { mapPaymentTerms, mapRateType } from '@/utils/shipmentFuncs'

import { List, ListItem } from '../List'
import { MouseWheelAnimation } from '../MouseWheelAnimation'
import { ShipperTooltip } from '../ShipperTooltip'

export const OverviewPanel = ({ load }: PropsWithLoad) => {
  const { accessorialName, equipmentName } = useTypeNames()
  const contextQuery = useAppContext()
  const locale = useLocale()
  const settings = useAccountSettings()

  const { equipment, pickupDate, selectedQuote, tracking, items } = load
  const chargeList = useChargeList(selectedQuote)
  const refNumsByType = useRefNumsByType(load.refNums)
  const hasCarrierInfo = !!(
    refNumsByType['carrier-name'] || refNumsByType['dot-num']
  )
  const equipmentType = selectedQuote?.equipmentType || equipment.type
  const paymentTerms = mapPaymentTerms(load)
  const rateType = mapRateType(load)
  const pricingType = load.isFreightCollect
    ? ''
    : displayPricingType(selectedQuote?.pricingType)
  const equipmentTypeName = equipmentName(equipmentType)
  const accessorialsDisplay = equipment.accessorials
    ?.map(a => accessorialName(a.key))
    .join(', ')
  const user = contextQuery.data?.user
  const totalWeight = totalShipmentWeight(load)
  const weightUOM = items?.[0]?.weightUOM ?? 'lbs'
  const totalWeightDisplay = totalWeight
    ? `${formatNumber(totalWeight)} ${weightUOM}`
    : ''
  const distanceDisplay = getDistanceDisplay(load, locale)

  const estimatedPickup = fvDateUtc(pickupDate)
  const actualPickup = tracking?.pickupDateActual
    ? fvDate(tracking.pickupDateActual)
    : undefined
  const showActualPickup =
    actualPickup &&
    !estimatedPickup.startOf('day').isSame(actualPickup.startOf('day'))
  return (
    <div className="min-h-0 b1350:row-start-1 b1350:col-span-full b1350:bg-[#f9f9f9] relative b1350:pr-10 b1350:border-b b1350:border-fv-gray b1350:px-4 bg-fv-blue-50 border-r border-fv-gray p-6 pt-8 overflow-auto b1350:p-0 b1350:border-r-0 fa-width-auto">
      <div className="b1350:py-6 b1350:border-fv-gray b1350:border-r b1350:overflow-auto">
        <List
          fcp0
          bare
          className="b1350:mb-0 b1350:flex b1350:items-center b1350:space-x-4 b1350:whitespace-nowrap"
        >
          <RefNumsEditor
            load={load}
            wrap={children => <ListItem>{children}</ListItem>}
          />
          {settings.glCode.enabled && (
            <ListItem>
              <GLCodeEditor load={load} className="underlined" />
            </ListItem>
          )}
          <ListItem divider className="b1350:hidden"></ListItem>
          <ListItem className="b1350:p-0 b1350:m-0 b1350:border-0">
            {showActualPickup && (
              <div className="b1350:flex-row b1350:items-center b1350:gap-y-0 b1350:pt-0 flex flex-col gap-x-3 gap-y-1">
                <div>
                  <Icon icon="calendar-day" className="text-fv-gray-dark" />
                  <span>
                    Est Pickup:{' '}
                    {estimatedPickup.local().format('ddd MMM D, YYYY')}
                  </span>
                </div>
                <div>
                  <Icon icon="calendar-day" className="text-fv-gray-dark" />
                  <span>
                    Act Pickup: {actualPickup.local().format('ddd MMM D, YYYY')}
                  </span>
                </div>
              </div>
            )}
            {!showActualPickup && (
              <div className="b1350:pt-0">
                <Icon icon="calendar-day" className="text-fv-gray-dark" />
                <span>{estimatedPickup.local().format('ddd MMM D, YYYY')}</span>
              </div>
            )}
          </ListItem>
          {hasCarrierInfo && (
            <ListItem className="b1350:p-0 b1350:m-0">
              <RefNumCarrierInfo refNumsByType={refNumsByType} />
            </ListItem>
          )}
          {equipmentTypeName && <ListItem>{equipmentTypeName}</ListItem>}
          {accessorialsDisplay && <ListItem>{accessorialsDisplay}</ListItem>}
          {totalWeightDisplay && <ListItem>{totalWeightDisplay}</ListItem>}
          {distanceDisplay && <ListItem>{distanceDisplay}</ListItem>}
          {!load.isFreightCollect && (
            <HasPermission name="view-rates">
              <ListItem>
                <ChargesToggle
                  className="font-oxygen"
                  theme="underlined"
                  chargeState={chargeList}
                  icon={false}
                  onClick={chargeList.toggleEditing}
                >
                  <FvNum
                    val={selectedQuote?.amount}
                    currency={selectedQuote?.currency}
                    includeEmptyDecimals
                  />
                </ChargesToggle>

                <ChargesSlider chargeState={chargeList} load={load} />
                {load.saltDetails?.result === 'approved' && (
                  <ShipperTooltip label="This is a S.A.L.T. rate">
                    <span className="ml-1">
                      <Icon icon="salt-shaker" />
                    </span>
                  </ShipperTooltip>
                )}
              </ListItem>
            </HasPermission>
          )}
          <ListItem divider className="b1350:hidden" />

          {!load.isFreightCollect && selectedQuote?.quoteNum && (
            <ListItem>
              <span>
                Quote# {selectedQuote.quoteNum}{' '}
                <ClipboardButton value={selectedQuote.quoteNum} />
              </span>
            </ListItem>
          )}
          {load.equipment.declaredValue && (
            <ListItem className="b1350:m-0 b1350:p-0">
              Declared value{' '}
              <FvNum
                val={load.equipment.declaredValue}
                currency={load.equipment.declaredValueCurrency}
                className="ml-1"
              />
            </ListItem>
          )}
          <HideForVendor>
            {rateType && <ListItem>{rateType}</ListItem>}
            {pricingType && <ListItem>{pricingType}</ListItem>}
            {paymentTerms && <ListItem>{paymentTerms}</ListItem>}
            <ListItem>
              <span>
                Booked by <LoadUserDisplay {...load.bookedBy} /> /{' '}
                <FvDate val={load.bookedDate} utc format="MMM D, YYYY" />
              </span>
            </ListItem>
            <ListItem className="last:pr-4">{user.company}</ListItem>

            {!!load.createdBy?.vendorId && (
              <>
                <ListItem className="b1350:m-0 b1350:p-0">
                  <span className="underline underline-offset-4 decoration-fv-orange">
                    Vendor created shipment
                  </span>
                </ListItem>
                <ListItem>
                  <VendorMarkupDescription noIcon load={load} />
                </ListItem>
              </>
            )}
          </HideForVendor>
        </List>
      </div>

      <MouseWheelAnimation className="hidden b1350:block right-2" />
    </div>
  )
}
